import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Settings,
  FileText,
  UserCog,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Mail,
  LogOut,
  BarChart3,
  Users,
  ShieldCheck,
  Activity,
} from 'lucide-react';

import { cn, showToastMessage, useRoleIds } from '@/utils';
import { NavItem, NavItemComponentProps, UserRole } from '@/types';

import { useLogoutMutation } from '@/store/api/authApi';
import { useDispatch, useSelector } from 'react-redux';
import { logout as clearAuth, RootState } from '@/store';
import { clearUser } from '@/store/slices/userSlice';
import apiSlice from '@/store/api/apiSlice';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { DW_AUTO_REFRESH_STORAGE_KEY } from '@/utils/storageKeys';
import { usePermissions } from '@/context/PermissionContext';

const isMobileViewport = () => window.innerWidth < 768;

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

function NavItemComponent({
  item,
  isOpen,
  level = 0,
  activeMenu,
  setActiveMenu,
  onToggle,
}: NavItemComponentProps) {
  const isExpanded = activeMenu === item.name;
  const location = useLocation();

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.href ? location.pathname === item.href : false;
  const hasActiveChild = item.children?.some(
    (child) => child.href && location.pathname.startsWith(child.href)
  );

  const triggerRef = useRef<HTMLDivElement>(null);
  const hideTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [showFlyout, setShowFlyout] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (hasActiveChild && isOpen) {
      setActiveMenu(item.name);
    }
  }, [hasActiveChild, isOpen]);

  useEffect(() => () => clearTimeout(hideTimeout.current), []);

  const handleClick = () => {
    if (hasChildren) {
      setActiveMenu(isExpanded ? null : item.name);
    }
  };

  const handleFlyoutEnter = () => {
    if (isOpen) return;
    clearTimeout(hideTimeout.current);
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setFlyoutPos({ top: rect.top, left: rect.right + 8 });
    }
    setShowFlyout(true);
  };

  const handleFlyoutLeave = () => {
    hideTimeout.current = setTimeout(() => setShowFlyout(false), 150);
  };

  const Icon = item?.icon;
  const indent = level * 16;

  if (hasChildren) {
    return (
      <div
        ref={triggerRef}
        className="relative"
        onMouseEnter={handleFlyoutEnter}
        onMouseLeave={handleFlyoutLeave}
      >
        <button
          onClick={handleClick}
          className={cn(
            'flex w-full items-center rounded-lg py-2 text-base font-medium transition-colors',
            isOpen ? 'px-3' : 'px-0',
            isActive || hasActiveChild
              ? 'bg-active-sidebar text-white font-semibold'
              : 'text-white/80 hover:bg-white/10 hover:text-white',
            !isOpen && 'justify-center'
          )}
          style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
        >
          {item.img
            ? item.img && (
                <img src={item.img} className={cn('h-5 w-5', isOpen ? 'mr-3' : 'h-5 w-5')} />
              )
            : Icon && <Icon className={cn('h-5 w-5', isOpen ? 'mr-3' : 'h-5 w-5')} />}

          {isOpen && (
            <>
              <span className="flex-1 text-left text-xs sm:text-sm">{item.name}</span>
              {isExpanded ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </>
          )}
        </button>

        {/* Flyout menu for collapsed sidebar, portaled so it isn't clipped by the sidebar's overflow-hidden/transform */}
        {!isOpen &&
          showFlyout &&
          flyoutPos &&
          createPortal(
            <div
              style={{ top: flyoutPos.top, left: flyoutPos.left }}
              className="fixed z-50 min-w-[200px] rounded-lg border border-white/10 bg-sidebar py-2 shadow-navy-shadow"
              onMouseEnter={handleFlyoutEnter}
              onMouseLeave={handleFlyoutLeave}
            >
              <div className="border-b border-white/10 px-3 py-2 text-xs font-semibold text-white sm:text-sm">
                {item.name}
              </div>
              {item.children!.map((child) => (
                <NavLink
                  key={child.name}
                  to={child.href!}
                  onClick={() => {
                    setShowFlyout(false);
                    if (isMobileViewport()) {
                      onToggle?.();
                    }
                  }}
                  className={({ isActive }) =>
                    cn(
                      'block px-3 py-2 text-xs transition-colors sm:text-sm',
                      isActive
                        ? 'bg-white/10 font-semibold text-white'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    )
                  }
                >
                  {child.name}
                </NavLink>
              ))}
            </div>,
            document.body
          )}

        {/* Tree Menu */}
        {isExpanded && isOpen && (
          <div className=" ml-6">
            <div className="relative border-l-2 border-white/20 space-y-4 py-2">
              {item.children!.map((child) => (
                <div key={child.name} className="relative">
                  {/* tick */}
                  <span className="absolute -left-0.5 top-2 h-0.5 w-4 bg-gray-600"></span>

                  {child.children ? (
                    <NavItemComponent
                      item={child}
                      isOpen={isOpen}
                      level={level + 1}
                      activeMenu={activeMenu}
                      setActiveMenu={setActiveMenu}
                      onToggle={onToggle}
                    />
                  ) : (
                    <NavLink
                      onClick={() => {
                        if (isMobileViewport()) {
                          onToggle?.();
                        }
                      }}
                      to={child.href!}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center text-xs sm:text-sm transition-colors',
                          isActive ? 'text-white font-semibold' : 'text-white/70 hover:text-white'
                        )
                      }
                    >
                      - {child.name}
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.href!}
      onClick={() => {
        if (isMobileViewport()) {
          onToggle?.();
        }
      }}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg py-1.5 sm:py-3 text-sm font-medium transition-colors',
          isOpen ? 'px-3' : 'px-0',
          isActive
            ? 'bg-active-sidebar text-white font-semibold border-l-2 border-l-[#fec733]'
            : 'text-white/80 hover:bg-white/10 hover:text-white',
          !isOpen && 'justify-center'
        )
      }
      style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
      title={!isOpen ? item.name : undefined}
    >
      {item.img
        ? item.img && <img src={item.img} className={cn('h-5 w-5', isOpen ? 'mr-3' : 'h-5 w-5')} />
        : Icon && <Icon className={cn('h-5 w-5', isOpen ? 'mr-3' : 'h-5 w-5')} />}

      {isOpen && <span className="text-xs sm:text-sm">{item.name}</span>}
    </NavLink>
  );
}

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const imagekit = import.meta.env.VITE_IMAGEKIT_URL;
  const [logout, { isLoading: logoutLoading }] = useLogoutMutation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const { hasModuleAccess } = usePermissions();
  const { isOwner, isDownlineUser, isRoleUser } = useRoleCheck();
  const navigate = useNavigate();
  const { superAdminID, adminID, superMasterId, subSuperMasterId, masterId, userId } = useRoleIds();
  const userRole = user?.data?.role?.name as UserRole | undefined;
  const DOWNLINE_ROLE_VISIBILITY: Record<UserRole, UserRole[]> = {
    OWNER: ['ADMIN', 'MASTER', 'USER'],
    ADMIN: ['MASTER', 'USER'],
    // 'SUPER MASTER': ['SUB SUPER MASTER', 'MASTER', 'USER'],
    // 'SUB SUPER MASTER': ['MASTER', 'USER'],
    MASTER: ['USER'],
    USER: [],
  };

  const effectiveRole = isRoleUser ? 'OWNER' : userRole;
  const visibleRoles: UserRole[] =
    effectiveRole && effectiveRole in DOWNLINE_ROLE_VISIBILITY
      ? DOWNLINE_ROLE_VISIBILITY[effectiveRole]
      : [];

  const downlineChildren: NavItem[] = [
    { id: 1, name: 'Admin', href: `/members/${adminID}`, role: 'ADMIN' },
    // { id: 2, name: 'Super Master', href: `/members/${superMasterId}`, role: 'SUPER MASTER' },
    // {
    //   id: 3,
    //   name: 'Sub Super Master',
    //   href: `/members/${subSuperMasterId}`,
    //   role: 'SUB SUPER MASTER',
    // },
    { id: 4, name: 'Master', href: `/members/${masterId}`, role: 'MASTER' },
    { id: 5, name: 'User', href: `/members/${userId}`, role: 'USER' },
    { id: 6, name: 'Settlement', href: `/settlement`, role: 'USER' },
  ].filter((item) => visibleRoles.includes(item.role as UserRole));

  const navigation: NavItem[] = [
    {
      id: 1,
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      img: `${imagekit}/sidebar/dashboard.svg`,
      permissionKey: 'Dashboard',
    },
    {
      id: 2,
      name: 'Downline',
      icon: BarChart3,
      img: `${imagekit}/sidebar/downline.svg`,
      permissionKey: 'Users',
      children: downlineChildren,
    },

    {
      id: 3,
      name: 'Sport Analysis',
      href: '/sport-analysis',
      icon: Activity,
      permissionKey: 'SportAnalysis',
    },
    {
      id: 17,
      name: 'Bet Control',
      icon: ShieldCheck,
      permissionKey: 'BetControl',
      href: '/bet-control',
    },

    {
      id: 4,
      name: 'My Reports',
      icon: FileText,
      img: `${imagekit}/sidebar/myreport.svg`,
      permissionKey: 'MyReport',

      children: [
        { id: 1, name: 'Bet List', href: '/bet-list' },
        { id: 2, name: 'Casino Bet List', href: '/casino-bet-list' },
        { id: 3, name: 'P/L Report By Downline', href: '/pl-by-downline' },
        { id: 4, name: 'P/L Report By Event', href: '/pl-by-event' },
        { id: 5, name: 'P/L Report By Player', href: '/pl-by-player' },
        { id: 6, name: 'P/L Report By Casino Downline', href: '/casinopl-by-downline' },
        { id: 7, name: 'P/L Report By Casino Players', href: '/casinopl-by-player' },
        { id: 8, name: 'Business Report', href: '/business-reports' },
        { id: 9, name: 'Party Win Loss', href: '/party-win-loss' },
        // {
        //   name: 'Inactive Members',
        //   href: '/inactive-members',
        // },
      ],
    },

    {
      id: 5,
      name: 'Event Management',
      img: `${imagekit}/sidebar/event.svg`,
      icon: CreditCard,
      permissionKey: 'EventManagement',
      children: [
        { id: 1, name: 'Events', href: '/event-management' },
        { id: 2, name: 'Bet Config', href: '/bet-setting' },
      ],
    },

    {
      id: 6,
      name: 'Casino Control',
      icon: Mail,
      href: '/casino-management',
      permissionKey: 'CasinoControl',

      img: `${imagekit}/sidebar/casino.svg`,
    },

    {
      id: 7,
      name: 'Content Management',
      href: '/content-management',
      img: `${imagekit}/sidebar/content.svg`,
      icon: UserCog,
      permissionKey: 'ContentManagement',
    },

    {
      id: 8,
      name: 'Bonus Management',
      icon: Settings,
      img: `${imagekit}/sidebar/bonus.svg`,
      permissionKey: 'Bonus',
      children: [
        {
          id: 1,
          name: 'Policy Management',
          href: '/bonus/policy-management',
        },
        {
          id: 2,
          name: 'Bonus Statement',
          href: '/bonus/statement',
        },
      ],
    },
    // {
    //   id: 9,
    //   name: 'Banking Management',
    //   icon: Settings,
    //   img: `${imagekit}/sidebar/bonus.svg`,
    //   permissionKey: 'BankingManagement',
    //   children: [
    //     {
    //       id: 1,
    //       name: 'Deposit/Withdraw Request',
    //       href: '/requests',
    //     },
    //     {
    //       id: 2,
    //       name: 'Manage Accounts',
    //       href: '/manage-account',
    //     },
    //     {
    //       id: 3,
    //       name: 'Crypto Conversion Settings',
    //       href: '/crypto-conversion',
    //     },
    //   ],
    // },
    {
      id: 10,
      name: 'Roles And Privileges',
      icon: Mail,
      href: '/roles-and-privileges',
      img: `${imagekit}/sidebar/casino.svg`,
      permissionKey: 'Roles Privileges',
    },
    // {
    //   id: 11,
    //   name: 'Commision',
    //   icon: CreditCard,
    //   permissionKey: 'Commission',
    //   children: [
    //     { id: 1, name: 'Setting', href: '/match-odds-commission-setting' },
    //     { id: 2, name: 'Report', href: '/match-odds-commission-report' },
    //   ],
    // },
    {
      id: 12,
      name: 'Account Statement',
      icon: Settings,
      href: '/rolewise-account-statement',
      permissionKey: 'Account Statement',
    },
    // {
    //   name: 'Account Statement',
    //   icon: Settings,
    //   href: '/account-statement',
    //   img: `${imagekit}/sidebar/account.svg`,
    //   onlyBanker: true,
    // },
    {
      id: 13,
      name: 'Export Report History',
      icon: Settings,
      href: '/export',
      img: `${imagekit}/sidebar/export.svg`,
      permissionKey: 'Export History',
    },

    {
      id: 14,
      name: 'Change Password',
      icon: Settings,
      img: `${imagekit}/sidebar/password.svg`,
      href: '/settings',
    },
    {
      id: 15,
      name: 'General Settings',
      icon: Settings,
      href: '/general-settings',
      permissionKey: 'General Settings',
    },
    {
      id: 16,
      name: 'Activity Log',
      img: `${imagekit}/sidebar/activity.svg`,
      icon: Settings,
      href: '/activity-logs',
      permissionKey: 'Activity Logs',
    },
  ];
  const agentNavigation: NavItem[] = [
    {
      id: 1,
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      img: `${imagekit}/sidebar/dashboard.svg`,
    },
    {
      id: 2,
      name: 'Downline',
      icon: BarChart3,
      img: `${imagekit}/sidebar/downline.svg`,
      children: downlineChildren,
    },
    // {
    //   id: 3,
    //   name: 'Risk Management',
    //   href: '/risk',
    //   icon: Users,
    //   img: `${imagekit}/sidebar/risk.svg`,
    // },
    {
      id: 3,
      name: 'Sport Analysis',
      href: '/sport-analysis',
      icon: Activity,
    },
    {
      id: 4,
      name: 'My Reports',
      icon: FileText,
      img: `${imagekit}/sidebar/myreport.svg`,
      children: [
        { id: 1, name: 'Bet List', href: '/bet-list' },
        { id: 2, name: 'Casino Bet List', href: '/casino-bet-list' },
        { id: 3, name: 'P/L Report By Downline', href: '/pl-by-downline' },
        { id: 4, name: 'P/L Report By Event', href: '/pl-by-event' },
        { id: 5, name: 'P/L Report By Player', href: '/pl-by-player' },
        { id: 6, name: 'P/L Report By Casino Downline', href: '/casinopl-by-downline' },
        { id: 7, name: 'P/L Report By Casino Players', href: '/casinopl-by-player' },
        { id: 8, name: 'Business Report', href: '/business-reports' },
        { id: 9, name: 'Party Win Loss', href: '/party-win-loss' },
        // {
        //   name: 'Inactive Members',
        //   href: '/inactive-members',
        // },
      ],
    },

    {
      id: 5,
      name: 'Account Statement',
      icon: Settings,
      href: '/rolewise-account-statement',
    },
    // {
    //   id: 6,
    //   name: 'Banking Management',
    //   icon: Settings,
    //   img: `${imagekit}/sidebar/bonus.svg`,
    //   children: [
    //     {
    //       id: 1,
    //       name: 'Deposit/Withdraw Request',
    //       href: '/requests',
    //     },
    //     {
    //       id: 2,
    //       name: 'Manage Accounts',
    //       href: '/manage-account',
    //     },
    //     {
    //       id: 3,
    //       name: 'Crypto Conversion Settings',
    //       href: '/crypto-conversion',
    //     },
    //   ],
    // },
    // {
    //   name: 'Account Statement',
    //   icon: Settings,
    //   href: '/account-statement',
    //   img: `${imagekit}/sidebar/account.svg`,
    //   onlyBanker: true,
    // },
    {
      id: 7,
      name: 'Export Report History',
      icon: Settings,
      href: '/export',
      img: `${imagekit}/sidebar/export.svg`,
    },
    {
      id: 8,
      name: 'Change Password',
      icon: Settings,
      img: `${imagekit}/sidebar/password.svg`,
      href: '/settings',
    },

    {
      id: 9,
      name: 'Activity Log',
      img: `${imagekit}/sidebar/activity.svg`,
      icon: Settings,
      href: '/activity-logs',
    },
  ];
  // const filteredNavigation = React.useMemo(() => {
  //   const role = user?.data?.role?.name;

  //   if (isOwner) {
  //     return navigation;
  //   }
  //   return navigation.filter((item) => {
  //     if (item.onlyBanker) return false;
  //     return true;
  //   });
  // }, [navigation, user]);

  const visibleNavItem = isDownlineUser
    ? agentNavigation
    : navigation.filter((item) => {
        // Owner-only items: hide for non-owners regardless of permissions
        if (item.onlyOwner && !isOwner) return false;
        // If no permissionKey, always show (e.g. Export, Activity Log)
        if (!item.permissionKey) return true;
        // Show only if at least one action is true in updatedAccess
        return hasModuleAccess(item.permissionKey, 'read');
      });

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      dispatch(clearAuth());
      dispatch(clearUser());
      localStorage.removeItem(DW_AUTO_REFRESH_STORAGE_KEY);
      apiSlice.util.resetApiState();
      showToastMessage('success', 'You have been logged out successfully.');
      navigate('/login');
    } catch (err: any) {
      showToastMessage('error', err?.data?.message || 'Failed to logout. Please try again.');
    }
  };
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  return (
    <>
      {/* Sidebar backdrop for mobile */}
      {isOpen && (
        <div className="fixed inset-0 z-20 bg-sidebar/20   lg:hidden" onClick={onToggle} />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex flex-col text-white bg-sidebar transition-all duration-300 overflow-hidden border-r border-r-[#EAAD07]/20',
          isOpen ? 'w-72 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div className="flex w-[150px] items-center bg-header p-4 pl-6">
          <img src={`${imagekit}/logowin365.png`} alt="" />

          {/* Toggle button - desktop only */}
          {/* <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn(
              'hidden lg:flex h-6 w-6',
              isOpen ? 'ml-auto' : 'fixed left-2 top-3 z-40 '
            )}
          >
            <reactIcons.squareBack
              className={`w-6 h-6 bg-white text-black p-0.5 rounded-sm transition-all duration-300 ${
                isOpen ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </Button> */}
        </div>

        {/* Input */}
        {/* <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search..." className="bg-transparent pl-9 text-gray border-green " />
        </div> */}

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto py-1.5  sm:py-3 px-1.5 sm:px-4">
          {visibleNavItem?.map((item) => (
            <NavItemComponent
              key={item.name}
              item={item}
              isOpen={isOpen}
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              onToggle={onToggle}
            />
          ))}
        </nav>

        {/* Logout */}

        <div
          onClick={handleLogout}
          className={` ${isOpen ? 'flex-start' : 'flex-center'}  gap-3 cursor-pointer text-xs sm:text-sm font-medium text-white/70 hover:text-white border-t border-t-white/20 py-2 sm:py-5 px-2 transition-colors`}
        >
          <LogOut className="h-5 w-5" />
          {isOpen && 'Logout'}
        </div>
      </div>
    </>
  );
}
